import type { CampaignAnalyticsKpi, CampaignAnalyticsKpiKey } from "@adpilot/shared";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";

export interface ConversionEfficiencyCardProps {
  /** Ads analytics KPIs for the dashboard scope (`useDashboardPerformance().ads.data?.kpis`). */
  kpis: CampaignAnalyticsKpi[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  className?: string;
}

type StageKey = Extract<CampaignAnalyticsKpiKey, "impressions" | "clicks" | "orders">;
type RateKey = Extract<CampaignAnalyticsKpiKey, "ctr" | "cvr">;

/** Funnel bands darken toward the top stage; band colors key the stage tiles. */
const STAGES: { key: StageKey; swatch: string; fill: string }[] = [
  { key: "impressions", swatch: "bg-chart-1", fill: "var(--color-chart-1)" },
  { key: "clicks", swatch: "bg-brand-8", fill: "var(--color-brand-8)" },
  { key: "orders", swatch: "bg-brand-7", fill: "var(--color-brand-7)" },
];
const RATES: { key: RateKey; digits: number }[] = [
  { key: "ctr", digits: 2 },
  { key: "cvr", digits: 1 },
];

const TILE = "flex h-[84px] min-w-0 flex-col justify-center gap-0.5 rounded-md bg-canvas px-3.5";
const VALUE = "truncate text-2xl font-semibold tracking-tight text-ink tabular-nums";

function deltaTone(kpi: CampaignAnalyticsKpi | undefined, delta: number | null): string {
  if (!kpi || delta == null || delta === 0 || kpi.semantics === "neutral")
    return "text-muted-foreground";
  const good = kpi.semantics === "positive-is-good" ? delta > 0 : delta < 0;
  return good ? "text-success-foreground" : "text-destructive-foreground";
}

function FunnelIcon() {
  return (
    <svg
      aria-hidden="true"
      width="22"
      height="20"
      viewBox="-2 -2 100 92"
      className="shrink-0"
      strokeWidth="6"
      strokeLinejoin="round"
    >
      <path
        d="M2.8 2 L93.2 2 L83.4 26 L12.6 26 Z"
        fill={STAGES[0]?.fill}
        stroke={STAGES[0]?.fill}
      />
      <path
        d="M15.1 32 L80.9 32 L71.1 56 L24.9 56 Z"
        fill={STAGES[1]?.fill}
        stroke={STAGES[1]?.fill}
      />
      <path
        d="M27.4 62 L68.6 62 L58.8 86 L37.2 86 Z"
        fill={STAGES[2]?.fill}
        stroke={STAGES[2]?.fill}
      />
    </svg>
  );
}

/** Brackets join each pair of adjacent stages and point at the rate they derive. */
function RateConnectors() {
  return (
    <svg
      aria-hidden="true"
      width="56"
      height="268"
      viewBox="0 0 56 268"
      className="absolute top-0 -left-2 overflow-visible"
      fill="none"
      stroke="var(--color-chart-1)"
      strokeWidth="1.25"
      strokeLinejoin="round"
    >
      <path d="M0 42 H28 V120 H0 M28 81 H52" />
      <path d="M0 148 H28 V226 H0 M28 187 H52" />
      <g fill="var(--color-chart-1)" stroke="none">
        <circle cx="0" cy="42" r="2.5" />
        <circle cx="0" cy="120" r="2.5" />
        <circle cx="0" cy="148" r="2.5" />
        <circle cx="0" cy="226" r="2.5" />
        <path d="M56 81 L49 77 L49 85 Z" />
        <path d="M56 187 L49 183 L49 191 Z" />
      </g>
    </svg>
  );
}

/** Impressions → clicks → ad orders, with the CTR and ad CVR each pair derives. */
export function ConversionEfficiencyCard({
  kpis,
  isLoading,
  isError,
  onRetry,
  className,
}: ConversionEfficiencyCardProps): React.ReactElement {
  const { t } = useTranslation("dashboard");
  const titleId = useId();
  const { formatNumber, formatPercent } = useFormatters();
  const byKey = new Map(kpis?.map((kpi) => [kpi.key, kpi]));

  const stageValue = (value: number | null | undefined) => {
    if (value == null) return "—";
    // Eight-digit counts no longer fit the tile; switch to compact past ten million.
    return value >= 10_000_000
      ? formatNumber(value, { notation: "compact", maximumFractionDigits: 1 })
      : formatNumber(value, { maximumFractionDigits: 0 });
  };
  const signed = (text: string, value: number) => (value > 0 ? `+${text}` : text);

  let body: React.ReactNode;
  if (isError && !kpis) {
    body = (
      <div className="flex h-[268px] items-center justify-center">
        <ErrorState message={t("overview.conversion.loadFailed")} onRetry={onRetry} />
      </div>
    );
  } else if (isLoading && !kpis) {
    body = (
      <div className="grid grid-cols-[minmax(0,148fr)_48px_minmax(0,144fr)]">
        <div className="flex flex-col gap-2">
          {STAGES.map((stage) => (
            <Skeleton key={stage.key} className="h-[84px] rounded-md" />
          ))}
        </div>
        <div />
        <div className="flex flex-col gap-[22px] pt-[39px]">
          {RATES.map((rate) => (
            <Skeleton key={rate.key} className="h-[84px] rounded-md" />
          ))}
        </div>
      </div>
    );
  } else {
    body = (
      <div className="grid grid-cols-[minmax(0,148fr)_48px_minmax(0,144fr)]">
        <ol className="flex flex-col gap-2">
          {STAGES.map((stage) => {
            const kpi = byKey.get(stage.key);
            const change = kpi?.change ?? null;
            return (
              <li key={stage.key} className={TILE}>
                <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <span aria-hidden className={cn("size-2 shrink-0 rounded-[2px]", stage.swatch)} />
                  {t(`overview.conversion.${stage.key}`)}
                </span>
                <span className={VALUE}>{stageValue(kpi?.value)}</span>
                <span className={cn("text-badge font-medium tabular-nums", deltaTone(kpi, change))}>
                  {change == null ? "—" : signed(formatPercent(change, 1), change)}
                  <span className="sr-only"> {t("overview.conversion.vsPrevious")}</span>
                </span>
              </li>
            );
          })}
        </ol>
        <div className="relative z-10">
          <RateConnectors />
        </div>
        <ul className="flex flex-col gap-[22px] pt-[39px]">
          {RATES.map((rate) => {
            const kpi = byKey.get(rate.key);
            const delta =
              kpi?.value != null && kpi.previousValue != null
                ? (kpi.value - kpi.previousValue) * 100
                : null;
            return (
              <li key={rate.key} className={TILE}>
                <span className="text-xs font-medium text-muted-foreground">
                  {t(`overview.conversion.${rate.key}`)}
                </span>
                <span className={VALUE}>
                  {kpi?.value == null ? "—" : formatPercent(kpi.value, rate.digits)}
                </span>
                <span className={cn("text-badge font-medium tabular-nums", deltaTone(kpi, delta))}>
                  {delta == null
                    ? "—"
                    : t("overview.conversion.points", {
                        value: signed(
                          formatNumber(delta, {
                            minimumFractionDigits: rate.digits,
                            maximumFractionDigits: rate.digits,
                          }),
                          delta,
                        ),
                      })}
                  <span className="sr-only"> {t("overview.conversion.vsPrevious")}</span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        "flex min-w-0 flex-col rounded-lg bg-surface shadow-[var(--paper-raised-shadow)]",
        className,
      )}
    >
      <header className="flex items-center justify-between gap-2 px-5 pt-4 pb-3">
        <h2 id={titleId} className="text-base font-semibold text-ink">
          {t("overview.conversion.title")}
        </h2>
        <FunnelIcon />
      </header>
      <div className="px-5 pt-1 pb-5">{body}</div>
    </section>
  );
}
